import { useQueryClient } from "@tanstack/react-query";
import { createContext, useContext, useEffect } from "react";
import SuperTokens from "supertokens-web-js";
import type { StorageHandler } from "supertokens-web-js/lib/build/windowHandler/types";
import EmailPassword from "supertokens-web-js/recipe/emailpassword";
import EmailVerification from "supertokens-web-js/recipe/emailverification";
import Passwordless from "supertokens-web-js/recipe/passwordless";
import Session from "supertokens-web-js/recipe/session";

import { Modal } from "@/components/Modal/Modal";
import { Button } from "@/design-system/Button";
import { useBool } from "@/hooks/useBool";
import { RawAccessTokenProvider, SuperTokensAccessTokenProvider } from "@/providers/AccessTokenProvider";
import { useConfig } from "@/providers/config/useConfig";
import { StartupLoading } from "@/providers/StartupLoading";
import { SESSION_IDENTITY_QUERY_KEY } from "@/queries/authentication/queryOptions";
import { useTranslation } from "@/translations";

import { createLocalDevelopmentAccessToken } from "./createLocalDevelopmentAccessToken";
import { isSignOutInProgress, useLogout } from "./useLogout";

// SuperTokens should only be initialized once. Even complete re-rendering of the app should not re-initialize it.
let superTokensInitialized: Date | undefined = undefined;
let sessionExpiredRef: { setSessionExpired?: () => void } = {};
const SessionExpiredContext = createContext<{ sessionExpired: boolean; onClose: () => void } | null>(null);

/** Renders the SDK notice in the active route's language rather than the anonymous bootstrap dictionary. */
export function SessionExpiredNotice(): React.ReactNode {
  const notice = useContext(SessionExpiredContext);

  return notice ? <SessionExpiredModal {...notice} /> : null;
}

export function SuperTokensProvider({ children }: { children: React.ReactNode }): React.ReactNode {
  if (import.meta.env.DEV && import.meta.env.VITE_LOCAL_DEV_AUTO_LOGIN === "true") {
    return (
      <RawAccessTokenProvider
        token={createLocalDevelopmentAccessToken(
          import.meta.env.VITE_LOCAL_DEV_USER_EMAIL,
          import.meta.env.VITE_LOCAL_DEV_USER_ID,
        )}
      >
        {children}
      </RawAccessTokenProvider>
    );
  }

  return <SuperTokensSessionProvider>{children}</SuperTokensSessionProvider>;
}

function SuperTokensSessionProvider({ children }: { children: React.ReactNode }): React.ReactNode {
  const logout = useLogout();
  const queryClient = useQueryClient();
  const authenticationServerRootUrl = useConfig("authenticationServerRootUrl");
  const newCoreApiRootUri = useConfig("newCoreApiRootUri");
  const [sessionExpired, sessionExpiredHandlers] = useBool();
  const [initialized, initializedHandlers] = useBool();

  useEffect(() => {
    sessionExpiredRef = {
      setSessionExpired: sessionExpiredHandlers.setTrue,
    };

    return () => {
      if (sessionExpiredRef.setSessionExpired === sessionExpiredHandlers.setTrue) {
        sessionExpiredRef = {};
      }
    };
  }, [sessionExpiredHandlers.setTrue]);

  useEffect(() => {
    initializedHandlers.setTrue();

    if (superTokensInitialized) {
      return;
    }

    SuperTokens.init({
      appInfo: {
        appName: "Area of People",
        apiDomain: authenticationServerRootUrl,
      },
      windowHandler: (original) => {
        return {
          ...original,
          // Override localstorage usage within supertokensso we don't have
          // localStorage edgecase crashes if localStorage is null. (can happen when safari private mode is enabled, or user has disabled localStorage)
          // https://github.com/supertokens/supertokens-website/issues/232#issuecomment-1719213092
          localStorage: createLocalStorageFallback(),
        };
      },
      recipeList: [
        EmailPassword.init(),
        EmailVerification.init(),
        Session.init({
          maxRetryAttemptsForSessionRefresh: 1,
          tokenTransferMethod: "header",
          sessionTokenBackendDomain: newCoreApiRootUri,
          onHandleEvent(event) {
            if (
              ["SESSION_CREATED", "REFRESH_SESSION", "ACCESS_TOKEN_PAYLOAD_UPDATED", "SIGN_OUT"].includes(event.action)
            ) {
              void queryClient.invalidateQueries({ queryKey: SESSION_IDENTITY_QUERY_KEY });
            }
            if (event.action === "UNAUTHORISED") {
              if (isSignOutInProgress()) {
                return;
              }

              // SuperTokens also triggers this event when a page loads without a session.
              // Always clean up session data, but only show the notice for expired or revoked sessions.
              if (
                event.sessionExpiredOrRevoked &&
                (!superTokensInitialized || new Date().getTime() - superTokensInitialized.getTime() > 5000)
              ) {
                sessionExpiredRef.setSessionExpired?.();
              }

              void logout({ keepUrl: true });
            }
          },
        }),
        Passwordless.init(),
      ],
    });

    superTokensInitialized = new Date();
  }, [
    authenticationServerRootUrl,
    initializedHandlers,
    logout,
    newCoreApiRootUri,
    sessionExpiredHandlers,
    queryClient,
  ]);

  if (!initialized) {
    return <StartupLoading />;
  }

  return (
    <SessionExpiredContext value={{ sessionExpired, onClose: sessionExpiredHandlers.setFalse }}>
      <SuperTokensAccessTokenProvider>{children}</SuperTokensAccessTokenProvider>
    </SessionExpiredContext>
  );
}

function SessionExpiredModal({
  sessionExpired,
  onClose,
}: {
  sessionExpired: boolean;
  onClose: () => void;
}): React.ReactNode {
  const { t } = useTranslation();

  return (
    <Modal.Root
      isOpened={sessionExpired}
      onOpenChange={(state) => {
        if (!state) {
          onClose();
        }
      }}
    >
      <Modal.Body>
        <article className="grid grid-cols-1 gap-y-2 px-8 py-4">
          <h2 className="max-w-sm justify-self-center text-heading-3">{t("component.session-expired-modal.title")}</h2>
          <p className="max-w-sm justify-self-center">{t("component.session-expired-modal.description")}</p>
          <div className="mt-4 flex flex-wrap justify-center gap-x-4 gap-y-2">
            <Button onClick={onClose} fullWidth="untilSm" variant="soft">
              {t("component.session-expired-modal.log-in")}
            </Button>
          </div>
        </article>
      </Modal.Body>
    </Modal.Root>
  );
}

function createLocalStorageFallback() {
  // In-memory fallback (per-tab)
  const memoryStore = new Map();

  const storageAvailable = (() => {
    try {
      if (!window.localStorage) return false;
      const test = "__st_test__";
      window.localStorage.setItem(test, test);
      window.localStorage.removeItem(test);

      return true;
    } catch {
      return false;
    }
  })();

  return {
    // oxlint-disable-next-line typescript/require-await
    clear: async () => {
      if (storageAvailable) {
        window.localStorage.clear();
      } else {
        memoryStore.clear();
      }
    },
    clearSync: () => {
      if (storageAvailable) {
        window.localStorage.clear();
      } else {
        memoryStore.clear();
      }
    },
    // oxlint-disable-next-line typescript/require-await
    key: (async (index) => {
      if (storageAvailable) {
        return window.localStorage.key(index);
      }

      return Array.from(memoryStore.keys())[index] ?? null;
    }) as StorageHandler["key"],
    keySync: ((index) => {
      if (storageAvailable) {
        return window.localStorage.key(index);
      }

      return Array.from(memoryStore.keys())[index] ?? null;
    }) as StorageHandler["keySync"],
    // oxlint-disable-next-line typescript/require-await
    setItem: (async (key, value) => {
      if (storageAvailable) {
        window.localStorage.setItem(key, value);
      } else {
        memoryStore.set(key, value);
      }
    }) as StorageHandler["setItem"],
    setItemSync: ((key, value) => {
      if (storageAvailable) {
        window.localStorage.setItem(key, value);
      } else {
        memoryStore.set(key, value);
      }
    }) as StorageHandler["setItemSync"],
    // oxlint-disable-next-line typescript/require-await
    getItem: (async (key) => {
      if (storageAvailable) {
        return window.localStorage.getItem(key);
      }

      return memoryStore.has(key) ? memoryStore.get(key) : null;
    }) as StorageHandler["getItem"],
    getItemSync: ((key) => {
      if (storageAvailable) {
        return window.localStorage.getItem(key);
      }

      return memoryStore.has(key) ? memoryStore.get(key) : null;
    }) as StorageHandler["getItemSync"],
    // oxlint-disable-next-line typescript/require-await
    removeItem: (async (key) => {
      if (storageAvailable) {
        window.localStorage.removeItem(key);
      } else {
        memoryStore.delete(key);
      }
    }) as StorageHandler["removeItem"],
    removeItemSync: ((key) => {
      if (storageAvailable) {
        window.localStorage.removeItem(key);
      } else {
        memoryStore.delete(key);
      }
    }) as StorageHandler["removeItemSync"],
  };
}
